import { useState } from 'react'
import { useOutsideClick } from '../hooks/useOutsideClick'
import { capabilityMatches } from '../capabilitySearch'

const SIZES = {
  sm: { btn: 'btn-sm', badge: 'badge-sm', checkbox: 'checkbox-sm', width: 'w-64', maxH: 'max-h-80', text: '' },
  xs: { btn: 'btn-xs', badge: 'badge-xs', checkbox: 'checkbox-xs', width: 'w-60', maxH: 'max-h-64', text: 'text-sm' },
}

// Multi-select capability dropdown: controlled open state (stays open while
// toggling checkboxes, closes on outside click), search box, and selected
// entries pinned to the top so what's active is always visible.
export default function CapabilityPicker({ selected, catalog, onChange, size = 'sm', header = null, onClear = null }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useOutsideClick(() => setOpen(false), open)
  const s = SIZES[size] ?? SIZES.sm

  function toggle(name) {
    onChange(
      selected.includes(name)
        ? selected.filter(n => n !== name)
        : [...selected, name],
    )
  }

  const matches = c => capabilityMatches(c, search)
  const selectedCaps = catalog.filter(c => selected.includes(c.name))
  const rest = catalog.filter(c => !selected.includes(c.name) && matches(c))

  const row = c => (
    <li key={c.id}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className={`checkbox ${s.checkbox}`}
          checked={selected.includes(c.name)}
          onChange={() => toggle(c.name)}
        />
        <span className={`flex-1 ${s.text}`}>{c.label}</span>
      </label>
    </li>
  )

  return (
    <div className={`dropdown ${open ? 'dropdown-open' : ''}`} ref={ref}>
      <button
        type="button"
        className={`btn ${s.btn} btn-outline`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        Capabilities
        {selected.length > 0 && (
          <span className={`badge badge-primary ${s.badge} ml-1`}>{selected.length}</span>
        )}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <ul className={`dropdown-content menu bg-base-200 rounded-box z-10 ${s.width} p-2 shadow-lg ${s.maxH} overflow-y-auto flex-nowrap`}>
        {header && (
          <li className="menu-title flex-row justify-between items-center">
            <span className="text-xs">{header}</span>
            {onClear && selected.length > 0 && (
              <button type="button" className="btn btn-xs btn-ghost" onClick={onClear}>Clear</button>
            )}
          </li>
        )}
        <li className="menu-title pt-0 pb-1">
          <input
            type="text"
            className="input input-xs input-bordered w-full"
            placeholder="Search capabilities…"
            aria-label="Search capabilities"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </li>
        {catalog.length === 0 ? (
          <li><span className="opacity-40 text-xs px-2 py-1">No capabilities defined</span></li>
        ) : selectedCaps.length === 0 && rest.length === 0 ? (
          <li><span className="opacity-40 text-xs px-2 py-1">No matches</span></li>
        ) : (
          <>
            {selectedCaps.map(row)}
            {selectedCaps.length > 0 && rest.length > 0 && <li className="divider my-0.5" />}
            {rest.map(row)}
          </>
        )}
      </ul>
    </div>
  )
}
